import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  LockClosedIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import AuthShell from './AuthShell';

const ModernLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <AuthShell subtitle="Sign in to continue to DM Admin">
      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <form id="login-form" onSubmit={handleSubmit} className="space-y-5">
        <div className="group">
          <label className="mb-2 block text-sm font-medium text-gray-700">Email Address</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <UserIcon className="h-5 w-5 text-primary-600" />
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full rounded-xl border border-gray-300 bg-white/80 py-3 pl-10 pr-3 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:bg-white focus:border-primary-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/20"
              placeholder="Enter your email"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="group">
          <label className="mb-2 block text-sm font-medium text-gray-700">Password</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LockClosedIcon className="h-5 w-5 text-primary-600" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full rounded-xl border border-gray-300 bg-white/80 py-3 pl-10 pr-12 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:bg-white focus:border-primary-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/20"
              placeholder="Enter your password"
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400 transition-colors hover:text-primary-600" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400 transition-colors hover:text-primary-600" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input type="checkbox" className="h-4 w-4 rounded border-gray-300 bg-white text-primary-600 focus:ring-primary-500" />
            <span className="ml-2 text-sm text-gray-600">Remember me</span>
          </label>
          <a href="#" className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-900 hover:underline">
            Forgot password?
          </a>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full rounded-xl py-3.5 px-4 text-base font-semibold text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            loading
              ? 'cursor-not-allowed bg-gray-400'
              : 'bg-gradient-to-br from-primary-900 to-primary-600 shadow-[0_4px_15px_rgba(33,57,102,0.3)] hover:-translate-y-0.5 hover:from-primary-950 hover:to-primary-800 hover:shadow-[0_6px_20px_rgba(33,57,102,0.4)] active:translate-y-0'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="-ml-1 mr-3 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Logging in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <div className="mt-8 space-y-2 text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <a href="#" className="font-medium text-primary-600 transition-colors hover:text-primary-900">
            Contact Administrator
          </a>
        </p>
        <p className="text-sm text-gray-600">
          Dealer or dryer manufacturer?{' '}
          <Link to="/vendor-login" className="font-medium text-primary-600 transition-colors hover:text-primary-900">
            Vendor sign in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
};

export default ModernLogin;
